import { useEffect, useMemo, useState } from 'react';

import { CloseOutlined } from '@ant-design/icons';
import { Button, Card, Select } from 'antd';
import { useTranslation } from 'react-i18next';

import VisitMapAndRoutes from 'scenes/VisitHistory/components/VisitMapAndRoutes';
import { formatInt } from 'scenes/VisitHistory/utils/format';
import { dayHeaderLabel } from 'scenes/VisitHistory/utils/mapData';
import { VisitSelection } from 'scenes/VisitHistory/utils/selection';
import { toggleSelection } from 'scenes/VisitHistory/utils/selectionScope';

import { TiendaPerfectaVisitHistoryStop } from 'models/TiendaPerfecta';

import { buildKpis, buildMapZones, lastRouteDates } from '../utils/storeVisits';

const I18N_BASE = 'scenes.store_visits.map';

export interface RouteFocus {
  routeId: string;
  routeCode: string;
  date: string;
}

interface StoreVisitsMapProps {
  stops: TiendaPerfectaVisitHistoryStop[];
  zoneId: string | null;
  onZoneChange: (zoneId: string) => void;
  focusDay: string | null;
  focusRoute: RouteFocus | null;
  onClearFocus: () => void;
  timeZone: string;
}

/**
 * Una zona a la vez para que el mapa se lea: por defecto la de más rutas y sus
 * últimos días con rutas; un día del gráfico o "Ver en mapa" lo acotan.
 */
function StoreVisitsMap({
  stops,
  zoneId,
  onZoneChange,
  focusDay,
  focusRoute,
  onClearFocus,
  timeZone,
}: StoreVisitsMapProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const zones = useMemo(() => buildMapZones(stops), [stops]);
  const zone =
    zones.find((z) => z.id === zoneId) ?? (zones.length ? zones[0] : null);

  const dates = useMemo(() => {
    if (focusRoute) return [focusRoute.date];
    if (focusDay) return [focusDay];
    return zone ? lastRouteDates(stops, zone.id) : [];
  }, [stops, zone, focusDay, focusRoute]);

  const mapStops = useMemo(
    () =>
      stops.filter(
        (s) =>
          s.zone.id === zone?.id &&
          dates.includes(s.plannedDate) &&
          (!focusRoute || s.route.id === focusRoute.routeId)
      ),
    [stops, zone, dates, focusRoute]
  );
  const kpis = useMemo(() => buildKpis(mapStops), [mapStops]);

  const queryKey = `${zone?.id}|${dates.join(',')}|${
    focusRoute?.routeId ?? ''
  }`;
  const [selection, setSelection] = useState<VisitSelection>(null);
  useEffect(() => setSelection(null), [queryKey]);

  const dayLabel = (date: string) => dayHeaderLabel(date, language);
  const scopeLabel = focusRoute
    ? t(`${I18N_BASE}.scope.route`, {
        route: focusRoute.routeCode,
        day: dayLabel(focusRoute.date),
      })
    : focusDay
    ? t(`${I18N_BASE}.scope.day`, { day: dayLabel(focusDay) })
    : dates.length
    ? t(`${I18N_BASE}.scope.last_days`, {
        count: dates.length,
        from: dayLabel(dates[0]),
        to: dayLabel(dates[dates.length - 1]),
      })
    : t(`${I18N_BASE}.scope.empty`);

  return (
    <Card
      className="store-visits-map"
      title={t(`${I18N_BASE}.title`)}
      extra={
        <span className="store-visits-map__zone">
          <span>{t(`${I18N_BASE}.zone`)}</span>
          <Select
            size="small"
            value={zone?.id}
            onChange={onZoneChange}
            disabled={zones.length === 0}
            style={{ minWidth: 180 }}
            options={zones.map((z) => ({
              value: z.id,
              label: t(`${I18N_BASE}.zone_option`, {
                name: z.name || t('scenes.visit_history.zone_unnamed'),
                count: z.routes,
              }),
            }))}
          />
        </span>
      }
    >
      <div className="store-visits-map__meta">
        <span>
          {scopeLabel}
          {' · '}
          {t(`${I18N_BASE}.meta`, {
            routes: formatInt(kpis.routes, language),
            stores: formatInt(kpis.planned, language),
            count: kpis.incidents,
            incidents: formatInt(kpis.incidents, language),
          })}
        </span>
        {(focusDay || focusRoute) && (
          <Button
            size="small"
            type="link"
            icon={<CloseOutlined />}
            onClick={onClearFocus}
          >
            {t(`${I18N_BASE}.clear`)}
          </Button>
        )}
      </div>
      <VisitMapAndRoutes
        stops={mapStops}
        queryKey={queryKey}
        routesTotal={kpis.routes}
        selection={selection}
        onSelect={(next) => setSelection((cur) => toggleSelection(cur, next))}
        timeZone={timeZone}
      />
    </Card>
  );
}

export default StoreVisitsMap;
